import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import apiService from "../../ApiService";
import { trackEvent } from "../../components/trackingUtils";
import html2canvas from "html2canvas";
import * as d3 from "d3";
import SEOHead from "../../common/SEOHead";
import { baseUrl } from "../../config";

export default function BlogSummaryWithInfographic() {
  const [url, setUrl] = useState("");
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [infographic, setInfographic] = useState(null);
  const [infographicLoading, setInfographicLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("summary");
  const [flashcards, setFlashcards] = useState(null);
  const [flashcardsLoading, setFlashcardsLoading] = useState(false);
  const infographicRef = useRef();

  const [articles, setArticles] = useState([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState("news");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [mindmap, setMindmap] = useState(null);
  const [mindmapLoading, setMindmapLoading] = useState(false);
  const mindmapRef = useRef();
  const PAGE_SIZE = 5;

  const rssFeeds = useMemo(
    () => ({
      technology: "https://feeds.feedburner.com/TechCrunch/",
      health: "https://rss.nytimes.com/services/xml/rss/nyt/Health.xml",
      business: "https://rss.nytimes.com/services/xml/rss/nyt/Business.xml",
      science: "https://rss.nytimes.com/services/xml/rss/nyt/Science.xml",
      news: "https://rss.nytimes.com/services/xml/rss/nyt/HomePage.xml",
    }),
    []
  );
  const handleRenderMindmap = async () => {
    if (!summary) return;
    setMindmapLoading(true);
    try {
      const resp = await apiService.fetchmindmap(summary.short_summary);
      setMindmap(resp);
    } catch (err) {
      console.error(err);
      setError("Failed to load mindmap.");
    } finally {
      setMindmapLoading(false);
    }
  };

  const handleDownloadInfographic = async () => {
    if (!infographicRef.current) return;

    const buttons = infographicRef.current.querySelectorAll(
      ".copy-btn, .download-btn"
    );
    buttons.forEach((btn) => (btn.style.display = "none"));

    const canvas = await html2canvas(infographicRef.current, { scale: 2 });
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `${summary.title}_infographic.png`;
    link.click();

    buttons.forEach((btn) => (btn.style.display = "inline-block"));
  };

  const stripHTML = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const fetchFeed = useCallback(
    async (cat) => {
      setFeedLoading(true);
      setArticles([]);
      setPage(1);
      try {
        const resp = await fetch(
          `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(
            rssFeeds[cat]
          )}`
        );
        const data = await resp.json();
        setArticles(data.status === "ok" ? data.items : []);
      } catch (err) {
        console.error(err);
        setArticles([]);
      } finally {
        setFeedLoading(false);
      }
    },
    [rssFeeds]
  );

  useEffect(() => {
    trackEvent("PageLoad", "", "AISummarizer");
    fetchFeed(category);
    setSearchQuery("");
  }, [category, fetchFeed]);

  const handleFetchSummary = async (link) => {
    setUrl(link);
    setSummary(null);
    setInfographic(null);
    setFlashcards(null);
    setMindmap(null);
    setError("");
    setActiveTab("summary");
    setSummaryLoading(true);

    try {
      const response = await apiService.fetchSummaryBlogs(link);
      if (response.status === "success") {
        setSummary(response.data.story_summary);
      } else {
        setError("Failed to fetch summary.");
      }
    } catch (err) {
      console.error(err);
      setError("Something went wrong.");
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleRenderInfographic = async () => {
    if (!summary) return;
    setInfographicLoading(true);
    try {
      const resp = await apiService.fetchInfoGraphic(summary.short_summary);
      setInfographic(resp);
    } catch (err) {
      console.error(err);
      setError("Failed to render infographic.");
    } finally {
      setInfographicLoading(false);
    }
  };
  const handleRenderFlashcards = async () => {
    if (!summary) return;

    setFlashcardsLoading(true);
    setError(""); // clear previous errors
    try {
      const resp = await apiService.fetchflashcard(summary.short_summary);

      if (resp?.flashcards?.length > 0) {
        setFlashcards(resp.flashcards);
        setCurrentSlide(0); // reset to first slide
      } else {
        setFlashcards([]);
        setError("No flashcards returned by API.");
      }
    } catch (err) {
      console.error(err);
      setFlashcards([]); // clear previous flashcards
      setError("Failed to fetch flashcards.");
    } finally {
      setFlashcardsLoading(false);
    }
  };
  function MindmapView({ data }) {
    const svgRef = useRef();
    const containerRef = useRef();

    useEffect(() => {
      if (!data) return;

      const container = containerRef.current;
      if (!container) return;

      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();

      const width = container.offsetWidth;
      const height = 600;
      svg.attr("width", width).attr("height", height);

      const g = svg
        .append("g")
        .attr("transform", `translate(${width / 2},${height / 2})`);

      // ✅ Step 1. Build hierarchy from data
      const root = d3.hierarchy(data, (d) => d.branches || d.children);

      // ✅ Step 2. Measure label sizes to adjust spacing dynamically
      const measureText = (text, fontSize = 13) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        ctx.font = `${fontSize}px sans-serif`;
        return ctx.measureText(text).width;
      };

      const allLabels = root
        .descendants()
        .map((d) => d.data.title || d.data.name || "");
      const longestWidth = Math.max(...allLabels.map((t) => measureText(t)));
      const avgLabelHeight = 18; // pixels per line
      const verticalSpacing = avgLabelHeight * 3; // enough space vertically
      const horizontalSpacing = Math.min(400, Math.max(200, longestWidth + 60));

      // ✅ Step 3. Apply D3 tree layout with dynamic spacing
      const treeLayout = d3
        .tree()
        .nodeSize([verticalSpacing, horizontalSpacing]);
      const treeData = treeLayout(root);

      const nodes = treeData.descendants();
      const links = treeData.links();

      // ✅ Step 4. Draw links (curved for readability)
      g.append("g")
        .selectAll("path")
        .data(links)
        .join("path")
        .attr("fill", "none")
        .attr("stroke", "#9ca3af")
        .attr("stroke-width", 1.5)
        .attr(
          "d",
          d3
            .linkHorizontal()
            .x((d) => d.y)
            .y((d) => d.x)
        );

      // ✅ Step 5. Draw node circles
      g.append("g")
        .selectAll("circle")
        .data(nodes)
        .join("circle")
        .attr("cx", (d) => d.y)
        .attr("cy", (d) => d.x)
        .attr("r", 6)
        .attr("fill", (d) => (d.depth === 0 ? "#2563eb" : "#60a5fa"))
        .attr("stroke", "white")
        .attr("stroke-width", 2);

      // ✅ Step 6. Add text labels with wrapping and dynamic offset
      g.append("g")
        .selectAll("text")
        .data(nodes)
        .join("text")
        .attr("x", (d) => (d.children ? d.y - 12 : d.y + 12))
        .attr("y", (d) => d.x)
        .attr("dy", "0.35em")
        .attr("text-anchor", (d) => (d.children ? "end" : "start"))
        .attr("font-size", (d) => (d.depth === 0 ? 16 : 13))
        .attr("fill", "#1e3a8a")
        .text((d) => d.data.title || d.data.name)
        .call(wrapText, 160)
        .each(function (d) {
          // Adjust Y spacing if multi-line
          const tspanCount = d3.select(this).selectAll("tspan").size();
          if (tspanCount > 1) d.x += (tspanCount - 1) * 10;
        });

      // ✅ Step 7. Zoom and Pan
      const zoom = d3
        .zoom()
        .scaleExtent([0.5, 3])
        .on("zoom", (event) => g.attr("transform", event.transform));
      svg.call(zoom);

      // ✅ Step 8. Resize observer for responsive width
      const resizeObserver = new ResizeObserver(() => {
        const newWidth = container.offsetWidth;
        svg.attr("width", newWidth);
      });
      resizeObserver.observe(container);

      return () => resizeObserver.disconnect();

      // ✅ Helper to wrap text dynamically
      function wrapText(text, width) {
        text.each(function () {
          const textEl = d3.select(this);
          const words = textEl.text().split(/\s+/).reverse();
          let word;
          let line = [];
          let lineNumber = 0;
          const lineHeight = 1.1;
          const y = textEl.attr("y");
          const dy = parseFloat(textEl.attr("dy"));
          let tspan = textEl
            .text(null)
            .append("tspan")
            .attr("x", textEl.attr("x"))
            .attr("y", y)
            .attr("dy", dy + "em");

          while ((word = words.pop())) {
            line.push(word);
            tspan.text(line.join(" "));
            if (tspan.node().getComputedTextLength() > width) {
              line.pop();
              tspan.text(line.join(" "));
              line = [word];
              tspan = textEl
                .append("tspan")
                .attr("x", textEl.attr("x"))
                .attr("y", y)
                .attr("dy", ++lineNumber * lineHeight + dy + "em")
                .text(word);
            }
          }
        });
      }
    }, [data]);

    return (
      <div
        ref={containerRef}
        className="bg-white border rounded-2xl shadow-md p-4 overflow-hidden"
        style={{
          cursor: "grab",
          width: "100%",
          height: "600px",
        }}
      >
        <svg ref={svgRef}></svg>
      </div>
    );
  }
  const handleDownload = async (ref, title) => {
    if (!ref.current) return;
    const canvas = await html2canvas(ref.current, { scale: 2 });
    const link = document.createElement("a");
    link.download = `${title || "content"}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const filteredArticles = articles
    .filter((a) => a.title.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate));

  const startIndex = (page - 1) * PAGE_SIZE;
  const pagedArticles = filteredArticles.slice(
    startIndex,
    startIndex + PAGE_SIZE
  );
  const totalPages = Math.ceil(filteredArticles.length / PAGE_SIZE);

  const isRecent = (pubDate) =>
    new Date() - new Date(pubDate) < 24 * 60 * 60 * 1000;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEOHead
        title={`SummarAI - AI Blog Summarizer | Turn Blogs into Insights`}
        description={`Transform long blog posts into concise summaries, flashcards, infographics, and mindmaps. Your AI assistant for content consumption.`}
        keywords={`AI, Blog Summary, Summarization, SummarAI, Flashcards, Mindmaps, Infographics`}
        ogImage={`${baseUrl}/screenshot-sass-tools.webp`}
        canonical={`${baseUrl}/tools/summarai`}
      />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-[#0b1220] via-[#1a2332] to-[#0b1220] text-white py-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-96 h-96 bg-[#E5C558]/10 rounded-full blur-3xl top-0 right-0 animate-pulse"></div>
          <div
            className="absolute w-96 h-96 bg-blue-500/10 rounded-full blur-3xl bottom-0 left-0 animate-pulse"
            style={{ animationDelay: "1s" }}
          ></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4">
          <div className="text-center">
            <div className="inline-flex items-center justify-center p-3 bg-[#E5C558]/20 rounded-2xl mb-6 backdrop-blur-sm">
              <svg
                className="w-10 h-10 text-[#E5C558]"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Summar
              </span>
              <span className="bg-gradient-to-r from-[#E5C558] to-[#d4b54d] bg-clip-text text-transparent">
                AI
              </span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              Transform long blog posts into concise summaries, interactive
              flashcards, beautiful infographics, and mind maps with the power
              of AI
            </p>

            {/* Feature Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {[
                {
                  icon: "📝",
                  title: "Smart Summaries",
                  desc: "AI-powered text extraction",
                },
                {
                  icon: "📊",
                  title: "Infographics",
                  desc: "Visual data representation",
                },
                {
                  icon: "📇",
                  title: "Flashcards",
                  desc: "Interactive learning cards",
                },
                {
                  icon: "🗺️",
                  title: "Mind Maps",
                  desc: "Visual knowledge mapping",
                },
              ].map((feature, idx) => (
                <div
                  key={idx}
                  className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10"
                >
                  <div className="text-2xl mb-2">{feature.icon}</div>
                  <h3 className="text-white font-semibold text-sm mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 text-xs">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Section - Light */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LEFT PANEL - Main Content */}
          <div className="lg:col-span-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-2xl p-8 shadow-xl">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-2 flex items-center gap-3">
                <div className="p-2 bg-[#E5C558]/20 rounded-xl">
                  <svg
                    className="w-6 h-6 text-[#E5C558]"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                AI Summarizer
              </h2>
              <p className="text-gray-600">
                Transform any blog URL into multiple AI-powered formats
              </p>
            </div>

            {/* Input + Summarize */}
            <div className="flex gap-3 mb-6">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Paste your blog URL here..."
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-4 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#E5C558] focus:border-transparent transition-all"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                  <svg
                    className="w-5 h-5 text-gray-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.56-.5-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.56.5.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.498-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              <button
                onClick={() => handleFetchSummary(url)}
                disabled={summaryLoading || !url.trim()}
                className="bg-gradient-to-r from-[#E5C558] to-[#d4b54d] hover:from-[#d4b54d] hover:to-[#E5C558] text-black font-semibold px-8 py-4 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg hover:shadow-[#E5C558]/25"
              >
                {summaryLoading ? (
                  <>
                    <svg
                      className="w-5 h-5 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      />
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                    </svg>
                    Summarize
                  </>
                )}
              </button>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400 flex items-center gap-3">
                <svg
                  className="w-5 h-5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                {error}
              </div>
            )}

            {/* Tabs */}
            {summary && (
              <div className="mb-6">
                <div className="flex gap-1 bg-gray-100 p-2 rounded-xl border border-gray-200">
                  {[
                    { id: "summary", label: "Summary", icon: "📝" },
                    { id: "infographic", label: "Infographic", icon: "📊" },
                    { id: "flashcards", label: "Flashcards", icon: "📇" },
                    { id: "mindmap", label: "Mind Map", icon: "🗺️" },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                        activeTab === tab.id
                          ? "bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-black shadow-lg"
                          : "text-gray-600 hover:text-gray-800 hover:bg-gray-200"
                      }`}
                      onClick={() => {
                        setActiveTab(tab.id);
                        if (tab.id === "infographic" && !infographic)
                          handleRenderInfographic();
                        if (tab.id === "flashcards" && !flashcards)
                          handleRenderFlashcards();
                        if (tab.id === "mindmap" && !mindmap)
                          handleRenderMindmap();
                      }}
                    >
                      <span className="text-lg">{tab.icon}</span>
                      <span className="hidden sm:inline">{tab.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Summary Tab */}
            {activeTab === "summary" && (
              <div className="space-y-6">
                {summaryLoading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="relative">
                      <div className="w-16 h-16 bg-[#E5C558]/20 rounded-full flex items-center justify-center mb-4">
                        <svg
                          className="w-8 h-8 text-[#E5C558] animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          />
                        </svg>
                      </div>
                    </div>
                    <p className="text-gray-700 text-lg font-medium">
                      📝 Generating Summary...
                    </p>
                    <p className="text-gray-600 text-sm mt-2">
                      Processing your content with AI
                    </p>
                  </div>
                ) : summary ? (
                  <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-gray-200 shadow-lg">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-3">
                      <div className="w-2 h-8 bg-gradient-to-b from-[#E5C558] to-[#d4b54d] rounded-full"></div>
                      {summary.title}
                    </h2>

                    <div className="max-w-none mb-6">
                      <p className="text-gray-700 leading-relaxed text-lg whitespace-pre-line">
                        {summary.short_summary}
                      </p>
                    </div>

                    {summary.bullets?.length > 0 && (
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                          <svg
                            className="w-5 h-5 text-[#E5C558]"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Key Points
                        </h3>
                        <ul className="space-y-2">
                          {summary.bullets.map((b, i) => (
                            <li
                              key={i}
                              className="flex items-start gap-3 text-gray-700"
                            >
                              <div className="w-2 h-2 bg-[#E5C558] rounded-full mt-2 flex-shrink-0"></div>
                              <span className="leading-relaxed">{b}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="flex gap-3">
                      <button
                        onClick={() =>
                          navigator.clipboard.writeText(
                            summary.title + "\n\n" + summary.short_summary
                          )
                        }
                        className="bg-gray-100 border border-gray-300 text-gray-700 px-6 py-3 rounded-xl hover:bg-gray-200 transition-all duration-200 flex items-center gap-2"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                          <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                        </svg>
                        Copy
                      </button>
                      <button
                        onClick={() =>
                          handleDownload(infographicRef, summary.title)
                        }
                        className="bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-black font-semibold px-6 py-3 rounded-xl hover:from-[#d4b54d] hover:to-[#E5C558] transition-all duration-200 flex items-center gap-2 shadow-lg"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Download
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-8 h-8 text-gray-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <p className="text-gray-400 text-lg">
                      Enter a blog URL and click "Summarize" to get started
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Infographic Tab */}
            {activeTab === "infographic" && summary && (
              <div className="space-y-6">
                {infographicLoading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-16 h-16 bg-[#E5C558]/20 rounded-full flex items-center justify-center mb-4">
                      <svg
                        className="w-8 h-8 text-[#E5C558] animate-pulse"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" />
                      </svg>
                    </div>
                    <p className="text-gray-100 text-lg font-medium">
                      📊 Generating Infographic...
                    </p>
                    <p className="text-gray-400 text-sm mt-2">
                      Creating visual representation
                    </p>
                  </div>
                ) : infographic ? (
                  <div
                    ref={infographicRef}
                    className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10"
                  >
                    <div className="text-center mb-8">
                      <h2 className="text-3xl font-bold text-gray-800 mb-4">
                        {infographic.title}
                      </h2>
                      <p className="text-gray-100 text-lg leading-relaxed max-w-3xl mx-auto">
                        {infographic.summary}
                      </p>
                    </div>

                    {infographic.sections?.map((section, i) => (
                      <div
                        key={i}
                        className="mb-8 bg-white/5 rounded-xl p-6 border border-white/10"
                      >
                        <h3 className="text-xl font-semibold text-[#E5C558] mb-4 flex items-center gap-2">
                          <div className="w-8 h-8 bg-[#E5C558]/20 rounded-lg flex items-center justify-center">
                            <span className="text-[#E5C558] font-bold">
                              {i + 1}
                            </span>
                          </div>
                          {section.heading}
                        </h3>
                        <ul className="space-y-2">
                          {section.points.map((p, j) => (
                            <li
                              key={j}
                              className="flex items-start gap-3 text-gray-100"
                            >
                              <div className="w-2 h-2 bg-[#E5C558] rounded-full mt-2 flex-shrink-0"></div>
                              <span className="leading-relaxed">{p}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}

                    {infographic.key_numbers?.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 my-8">
                        {infographic.key_numbers.map((k, idx) => (
                          <div
                            key={idx}
                            className="bg-gradient-to-br from-[#E5C558]/20 to-[#d4b54d]/10 border border-[#E5C558]/30 rounded-xl p-4 text-center backdrop-blur-sm"
                          >
                            <p className="text-2xl font-bold text-[#E5C558] mb-1">
                              {k.value}
                            </p>
                            <p className="text-sm text-gray-400">{k.label}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {infographic.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-6">
                        {infographic.tags.map((tag, i) => (
                          <span
                            key={i}
                            className="px-3 py-1 bg-[#E5C558]/20 text-[#E5C558] rounded-lg text-sm font-medium border border-[#E5C558]/30"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Copy & Download */}
                    <div className="flex gap-3 pt-6 border-t border-white/10">
                      <button
                        onClick={() =>
                          navigator.clipboard.writeText(
                            infographic.title +
                              "\n\n" +
                              infographic.summary +
                              "\n\n" +
                              infographic.sections
                                .map(
                                  (s) =>
                                    s.heading + "\n• " + s.points.join("\n• ")
                                )
                                .join("\n\n")
                          )
                        }
                        className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-6 py-3 rounded-xl hover:bg-white/20 transition-all duration-200 flex items-center gap-2"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                          <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                        </svg>
                        Copy
                      </button>
                      <button
                        onClick={handleDownloadInfographic}
                        className="bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-black font-semibold px-6 py-3 rounded-xl hover:from-[#d4b54d] hover:to-[#E5C558] transition-all duration-200 flex items-center gap-2 shadow-lg"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Download
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-8 h-8 text-gray-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" />
                      </svg>
                    </div>
                    <p className="text-gray-400 text-lg">
                      Click the Infographic tab to generate visual content
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Flashcards Tab */}
            {activeTab === "flashcards" && summary && (
              <div className="space-y-6">
                {flashcardsLoading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-16 h-16 bg-[#E5C558]/20 rounded-full flex items-center justify-center mb-4">
                      <svg
                        className="w-8 h-8 text-[#E5C558] animate-pulse"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <rect
                          x="3"
                          y="4"
                          width="14"
                          height="10"
                          rx="2"
                          ry="2"
                          stroke="currentColor"
                          strokeWidth="2"
                          fill="none"
                        />
                        <line
                          x1="3"
                          y1="9"
                          x2="17"
                          y2="9"
                          stroke="currentColor"
                          strokeWidth="1"
                        />
                      </svg>
                    </div>
                    <p className="text-gray-100 text-lg font-medium">
                      📇 Generating Flashcards...
                    </p>
                    <p className="text-gray-400 text-sm mt-2">
                      Creating interactive learning cards
                    </p>
                  </div>
                ) : flashcards?.length > 0 ? (
                  <div className="flex flex-col items-center">
                    <div
                      className="relative w-full max-w-lg h-80 select-none"
                      style={{ perspective: "1200px" }}
                    >
                      {/* Card wrapper */}
                      <div
                        onClick={() => setIsFlipped(!isFlipped)}
                        style={{
                          width: "100%",
                          height: "100%",
                          position: "relative",
                          transition: "transform 0.6s ease",
                          transformStyle: "preserve-3d",
                          transform: isFlipped
                            ? "rotateY(180deg)"
                            : "rotateY(0deg)",
                          cursor: "pointer",
                        }}
                      >
                        {/* FRONT */}
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            WebkitBackfaceVisibility: "hidden",
                            backfaceVisibility: "hidden",
                          }}
                          className="rounded-2xl bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 flex flex-col justify-center items-center p-8 text-center shadow-lg"
                        >
                          <div className="w-12 h-12 bg-[#E5C558]/20 rounded-full flex items-center justify-center mb-4">
                            <span className="text-2xl">📝</span>
                          </div>
                          <h3 className="text-2xl font-semibold text-white mb-6 leading-tight">
                            {flashcards[currentSlide].title}
                          </h3>

                          {flashcards[currentSlide].tags &&
                          flashcards[currentSlide].tags.length > 0 ? (
                            <div className="flex flex-wrap justify-center gap-2 mt-2">
                              {flashcards[currentSlide].tags.map((tag, i) => (
                                <span
                                  key={i}
                                  className="px-3 py-1 bg-[#E5C558]/20 text-[#E5C558] rounded-full text-sm font-medium border border-[#E5C558]/30"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-100 whitespace-pre-line leading-relaxed">
                              {flashcards[currentSlide].content ||
                                "No tags available."}
                            </p>
                          )}

                          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                            <p className="text-sm text-[#E5C558] flex items-center gap-2">
                              <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                              </svg>
                              Click to flip
                            </p>
                          </div>
                        </div>

                        {/* BACK */}
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            WebkitBackfaceVisibility: "hidden",
                            backfaceVisibility: "hidden",
                            transform: "rotateY(180deg)",
                          }}
                          className="rounded-2xl bg-gradient-to-br from-[#E5C558]/20 to-[#d4b54d]/10 backdrop-blur-sm border border-[#E5C558]/30 flex flex-col justify-center items-center p-8 text-center shadow-lg"
                        >
                          <div className="w-12 h-12 bg-[#E5C558]/30 rounded-full flex items-center justify-center mb-4">
                            <span className="text-2xl">💡</span>
                          </div>
                          <h3 className="text-xl font-semibold text-white mb-6">
                            {flashcards[currentSlide].title}
                          </h3>
                          <p className="text-gray-200 whitespace-pre-line leading-relaxed">
                            {flashcards[currentSlide].content ||
                              "No additional info provided."}
                          </p>

                          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                            <p className="text-sm text-[#E5C558] flex items-center gap-2">
                              <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                              </svg>
                              Click to flip back
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Navigation Buttons */}
                      <div className="absolute -left-16 top-1/2 transform -translate-y-1/2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsFlipped(false);
                            setCurrentSlide((s) => Math.max(0, s - 1));
                          }}
                          disabled={currentSlide === 0}
                          className="bg-white/10 backdrop-blur-sm hover:bg-white/20 border border-white/20 text-white w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>

                      <div className="absolute -right-16 top-1/2 transform -translate-y-1/2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsFlipped(false);
                            setCurrentSlide((s) =>
                              Math.min(flashcards.length - 1, s + 1)
                            );
                          }}
                          disabled={currentSlide === flashcards.length - 1}
                          className="bg-white/10 backdrop-blur-sm hover:bg-white/20 border border-white/20 text-white w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>

                      {/* Slide Indicator */}
                      <div className="absolute -bottom-12 w-full text-center">
                        <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
                          <span className="text-sm text-[#E5C558] font-medium">
                            {currentSlide === 0
                              ? "Start"
                              : currentSlide === flashcards.length - 1
                              ? "End"
                              : `Card ${currentSlide + 1} of ${
                                  flashcards.length
                                }`}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-8 h-8 text-gray-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <rect
                          x="3"
                          y="4"
                          width="14"
                          height="10"
                          rx="2"
                          ry="2"
                          stroke="currentColor"
                          strokeWidth="2"
                          fill="none"
                        />
                      </svg>
                    </div>
                    <p className="text-gray-400 text-lg">
                      No flashcards available
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Mindmap Tab */}
            {activeTab === "mindmap" && summary && (
              <div className="space-y-6">
                {mindmapLoading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-16 h-16 bg-[#E5C558]/20 rounded-full flex items-center justify-center mb-4">
                      <svg
                        className="w-8 h-8 text-[#E5C558] animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        />
                      </svg>
                    </div>
                    <p className="text-gray-100 text-lg font-medium">
                      🗺️ Generating Mindmap...
                    </p>
                    <p className="text-gray-400 text-sm mt-2">
                      Creating visual knowledge map
                    </p>
                  </div>
                ) : mindmap ? (
                  <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                    <div className="mb-6 text-center">
                      <h3 className="text-2xl font-bold text-white mb-2 flex items-center justify-center gap-3">
                        <div className="w-10 h-10 bg-[#E5C558]/20 rounded-xl flex items-center justify-center">
                          <span className="text-2xl">🗺️</span>
                        </div>
                        Knowledge Map
                      </h3>
                      <p className="text-gray-400">
                        Interactive visualization of key concepts
                      </p>
                    </div>

                    <div
                      ref={mindmapRef}
                      className="w-full bg-white/5 rounded-xl border border-white/10 overflow-hidden"
                    >
                      <MindmapView data={mindmap} />
                    </div>

                    <div className="flex justify-center mt-6">
                      <button
                        onClick={async () => {
                          if (!mindmapRef.current) return;

                          const svg = mindmapRef.current.querySelector("svg");
                          if (!svg) return;

                          // Serialize SVG to a string
                          const serializer = new XMLSerializer();
                          const svgString = serializer.serializeToString(svg);

                          // Create a Blob from the SVG string
                          const blob = new Blob([svgString], {
                            type: "image/svg+xml;charset=utf-8",
                          });
                          const url = URL.createObjectURL(blob);

                          // Create an image element to render SVG
                          const img = new Image();
                          img.onload = async () => {
                            const canvas = document.createElement("canvas");
                            canvas.width = img.width * 2; // high-res
                            canvas.height = img.height * 2;
                            const ctx = canvas.getContext("2d");
                            ctx.scale(2, 2); // upscale for better resolution
                            ctx.drawImage(img, 0, 0);

                            const link = document.createElement("a");
                            link.download = `${summary.title}_mindmap.png`;
                            link.href = canvas.toDataURL("image/png");
                            link.click();

                            URL.revokeObjectURL(url);
                          };
                          img.src = url;
                        }}
                        className="bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-black font-semibold px-8 py-4 rounded-xl hover:from-[#d4b54d] hover:to-[#E5C558] transition-all duration-200 flex items-center gap-3 shadow-lg"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Download Mindmap
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-8 h-8 text-gray-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                        <path
                          d="M12 2v4m0 12v4m10-10h-4M6 12H2m15.07-7.07l-2.83 2.83M6.76 17.24l-2.83 2.83M17.24 17.24l2.83 2.83M6.76 6.76L3.93 3.93"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <p className="text-gray-400 text-lg">
                      Click the Mind Map tab to load visual representation
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT PANEL - RSS Feed Explorer */}
          <div className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 shadow-xl">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2 flex items-center gap-3">
                <div className="p-2 bg-[#E5C558]/20 rounded-xl">
                  <svg
                    className="w-6 h-6 text-[#E5C558]"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                Explore Topics
              </h2>
              <p className="text-gray-600">
                Browse trending articles from various sources
              </p>
            </div>

            {/* Category Buttons */}
            <div className="flex flex-wrap gap-2 mb-6">
              {[
                "news",
                ...Object.keys(rssFeeds).filter((cat) => cat !== "news"),
              ].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
                    category === cat
                      ? "bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-black shadow-lg"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800 border border-gray-200"
                  }`}
                >
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </button>
              ))}
            </div>

            {/* Search Input */}
            <div className="relative mb-6">
              <input
                type="text"
                placeholder={`Search ${category} articles...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 pl-12 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#E5C558] focus:border-transparent transition-all"
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-4">
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>

            {/* Articles List */}
            <div className="flex-1">
              {feedLoading ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-[#E5C558]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-6 h-6 text-[#E5C558] animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-600">
                    Loading {category} articles...
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pagedArticles.map((art, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleFetchSummary(art.link)}
                      className={`p-4 rounded-xl cursor-pointer transition-all duration-200 border ${
                        isRecent(art.pubDate)
                          ? "bg-[#E5C558]/10 border-[#E5C558]/30 hover:bg-[#E5C558]/20"
                          : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                            isRecent(art.pubDate)
                              ? "bg-[#E5C558]"
                              : "bg-gray-500"
                          }`}
                        ></div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-800 mb-2 leading-tight hover:text-[#E5C558] transition-colors">
                            {art.title}
                          </h3>
                          <p className="text-gray-600 text-sm mb-2 line-clamp-2 leading-relaxed">
                            {stripHTML(art.description || art.content)}
                          </p>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">
                              {new Date(art.pubDate).toLocaleDateString()}
                            </span>
                            {isRecent(art.pubDate) && (
                              <span className="inline-flex items-center px-2 py-1 bg-[#E5C558]/20 text-[#E5C558] text-xs rounded-full font-medium">
                                ⚡ New
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="bg-gray-100 border border-gray-200 text-gray-700 px-4 py-2 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-all duration-200 flex items-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Prev
                </button>

                <span className="text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-lg border border-gray-200">
                  Page {page} of {totalPages}
                </span>

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="bg-gray-100 border border-gray-200 text-gray-700 px-4 py-2 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-all duration-200 flex items-center gap-2"
                >
                  Next
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
